import { useEffect, useRef, useState } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";

interface IProps {
  /** Identificador opcional para enfocar o localizar el input */
  id?: string;
  /** Lo que se escribió la última vez, para no perderlo al volver. */
  textoInicial?: string;
  /** Dice en qué se busca, que cambia según el listado. */
  placeholder?: string;
  /** Se llama con el texto que toca buscar. */
  alBuscar: (texto: string) => void;
}

/** Lo que se espera tras la última tecla antes de filtrar. */
export const ESPERA_ANTES_DE_FILTRAR = 250;

/**
 * Un solo campo que filtra un listado mientras se escribe.
 *
 * Los listados tenían dos o tres desplegables, un botón de buscar y otro de
 * limpiar, dentro de un recuadro alto que empujaba la tabla fuera de la
 * pantalla. Hacían falta tres gestos para algo que se resuelve escribiendo, y
 * el espacio que ocupaba era el que le faltaba a la tabla.
 *
 * El texto busca a la vez en varios campos, así que no hay que saber de
 * antemano en cuál está lo que uno recuerda. Cuáles son lo decide quien lo
 * usa; aquí solo se entrega lo escrito.
 *
 * Se espera un cuarto de segundo tras la última tecla: filtrar en cada
 * pulsación rehace la tabla entera y se siente a tirones. Vaciar el campo no
 * espera, porque ahí lo que se quiere es volver al listado completo.
 */
export const CampoDeBusqueda = ({
  id = "input-busqueda-principal",
  textoInicial = "",
  placeholder = "Busca por nombre, línea, expresión o entidad",
  alBuscar,
}: IProps) => {
  const [texto, setTexto] = useState(textoInicial);
  const temporizador = useRef<any>(null);

  // Si el campo se desmonta con una búsqueda en espera, no se dispara.
  useEffect(
    () => () => {
      if (temporizador.current) clearTimeout(temporizador.current);
    },
    []
  );

  // Si textoInicial cambia desde el exterior (ej. un chip de filtro rápido), se actualiza.
  useEffect(() => {
    setTexto(textoInicial);
  }, [textoInicial]);

  const cambiar = (nuevo: string) => {
    setTexto(nuevo);
    if (temporizador.current) clearTimeout(temporizador.current);

    if (nuevo.trim() === "") {
      alBuscar("");
      return;
    }

    temporizador.current = setTimeout(() => alBuscar(nuevo), ESPERA_ANTES_DE_FILTRAR);
  };

  // Va suelto: lo coloca quien lo usa, en la fila de los botones de vista.
  return (
    <div className="modern-search-input-wrapper">
      <Input
        id={id}
        allowClear
        size="large"
        className="modern-search-input"
        value={texto}
        prefix={<SearchOutlined className="font-color-707070 search-icon-hover" />}
        placeholder={placeholder}
        aria-label={placeholder}
        onChange={(e) => cambiar(e.target.value)}
        onPressEnter={() => {
          if (temporizador.current) clearTimeout(temporizador.current);
          alBuscar(texto);
        }}
      />
    </div>
  );
};
